import { parse } from 'csv-parse/sync';
import { stringify as csvStringify } from 'csv-stringify/sync';
import * as _ from 'lodash';
import { json2xml, xml2json } from 'xml-js';
import * as YAML from 'yaml';

import { parseAsBoolean, BooleanBasic } from '../common/boolean';
import { getErrorMessage, ParserError, SerializerError } from '../errors';

/**
 * Determine if a value is an empty string, array, object or falsy.
 * @param value Value to check.
 *
 * @returns True if the value is empty.
 */
export function isBlank<T>(value: T | T[]): boolean {
  if (Array.isArray(value)) {
    return value.length == 0;
  }
  return (
    !value ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '') ||
    (typeof value === 'object' && Object.keys(value).length == 0)
  );
}

/**
 * Determine if a value is an empty string, array, object.
 * @param value Value to check.
 *
 * @returns True if the value is empty.
 */
export function isEmpty<T>(value: T | T[]): boolean {
  if (typeof value === 'boolean') {
    return false;
  }
  return isBlank(value);
}

/**
 * Check if value is an array.
 * @param value The value to check.
 *
 * @returns True if the value is an array. False otherwise.
 */
export function isArray<T>(value: T): boolean {
  return _.isArray(value);
}

/**
 * Check if value is a string.
 * @param value The value to check.
 *
 * @returns True if the value is a string. False otherwise.
 *
 */
export function isString<T>(value: T): boolean {
  return _.isString(value);
}

/**
 * Check if value is null or undefined.
 * @param value The value to check.
 *
 * @returns True if the value is null or undefined. False otherwise.
 *
 */
export function isNullOrUndefined<T>(value: T): boolean {
  return _.isNull(value) || _.isUndefined(value);
}

/**
 * Check if a value is an object.
 * @param value The value to check.
 *
 * @returns True if the value is an object. False otherwise.
 *
 */
export function isObject<T>(value: T): boolean {
  return _.isObject(value) && !_.isArray(value);
}

/**
 * Parse an XML string.
 * @param xml The XML string to parse.
 *
 * @returns The parsed XML value.
 */
export function fromXml(xml: string): object {
  try {
    const jsonString = xml2json(xml, { compact: true });
    return jsonString ? (JSON.parse(jsonString) as object) : {};
  } catch (e) {
    throw new ParserError(getErrorMessage(e, { excludeErrorName: true }));
  }
}

/**
 * Parse a YAML string.
 * @param yaml The YAML string to parse.
 *
 * @returns The parsed YAML value.
 */
export function fromYaml(yaml: string): object {
  try {
    return YAML.parse(yaml) as object;
  } catch (e) {
    throw new ParserError(getErrorMessage(e, { excludeErrorName: true }));
  }
}

/**
 * Parse a CSV string.
 * @param csv The CSV string to parse.
 * @param headersIncluded If the first line of the CSV string is a list of header names.
 *
 * @returns The parsed CSV value.
 */
export function fromCsv(csv: string, headersIncluded: boolean = false): object[] {
  try {
    return parse(csv, {
      columns: headersIncluded,
      skip_empty_lines: true
    }) as object[];
  } catch (e) {
    throw new ParserError(getErrorMessage(e, { excludeErrorName: true }));
  }
}

/**
 * Parse a CSV string.
 * @param csv The CSV string to parse.
 * @param headersIncluded If the first line of the CSV string is a list of header names.
 *
 * @returns The parsed CSV value.
 */
export function fromCsvBasic<H extends `${BooleanBasic}`>(
  csv: string,
  headersIncluded: BooleanBasic | H = BooleanBasic.no
): object[] | string[][] {
  try {
    const hasHeaders = parseAsBoolean(headersIncluded);
    return fromCsv(csv, hasHeaders);
  } catch (e) {
    throw new ParserError(getErrorMessage(e, { excludeErrorName: true }));
  }
}

/**
 * Serialize a value to XML.
 * @param value The value to serialize.
 *
 * @returns The XML string.
 */
export function toXml(value: object): string {
  try {
    const jsonString = JSON.stringify(value);
    return json2xml(jsonString, { compact: true }) || '';
  } catch (e) {
    throw new SerializerError(getErrorMessage(e, { excludeErrorName: true }));
  }
}

/**
 * Serialize a value to YAML.
 * @param value The value to serialize.
 *
 * @returns The YAML string.
 */
export function toYaml(value: object): string {
  try {
    return YAML.stringify(value);
  } catch (e) {
    throw new SerializerError(getErrorMessage(e, { excludeErrorName: true }));
  }
}

/**
 * Serialize a value to CSV.
 * @param value The value to serialize.
 *
 * @returns The CSV string.
 */
export function toCsv(value: { [key: string]: unknown }[]): string {
  try {
    if (value.length === 0) {
      return '';
    }
    return csvStringify(value, { header: true });
  } catch (e) {
    throw new SerializerError(getErrorMessage(e, { excludeErrorName: true }));
  }
}
